import React, { createContext, useContext, useState, useEffect } from 'react';

interface TreasuryContextType {
  totalValue: number;
  suiStaked: number;
  usdcReserve: number;
  apy: number;
  loading: boolean;
  refresh: () => void;
}

const TreasuryContext = createContext<TreasuryContextType | undefined>(undefined);

export const useTreasury = () => {
  const context = useContext(TreasuryContext);
  if (context === undefined) {
    throw new Error('useTreasury must be used within a TreasuryProvider');
  }
  return context;
};

export const TreasuryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [totalValue, setTotalValue] = useState(7200000);
  const [suiStaked, setSuiStaked] = useState(4680000);
  const [usdcReserve, setUsdcReserve] = useState(2520000);
  const [apy, setApy] = useState(15.7);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Simulate small variations in values
      setTotalValue(prev => prev + (Math.random() - 0.5) * 100000);
      setApy(prev => prev + (Math.random() - 0.5) * 0.5);
    } catch (error) {
      console.error('Failed to refresh treasury data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(refresh, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  return (
    <TreasuryContext.Provider value={{
      totalValue,
      suiStaked,
      usdcReserve,
      apy,
      loading,
      refresh
    }}>
      {children}
    </TreasuryContext.Provider>
  );
};
